import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, MessageCircle, Zap, Coins, BarChart3, Trophy, ChevronRight, Search, Target, Users } from 'lucide-react';
import { motion } from 'framer-motion';
import { getBrazilianStatus } from '../../utils/detectBrazil';
import { pickstarOnboardingPtBR } from '../../components/mappings/pickstarOnboarding.pt-br';
import SEOFooter from '../../components/SEO/SEOFooter';

const PickStarOnboarding = () => {
  const navigate = useNavigate();
  const isBrazilian = getBrazilianStatus();
  const t = (key) => isBrazilian && pickstarOnboardingPtBR[key] ? pickstarOnboardingPtBR[key] : key;

  const steps = [
    {
      step: "01",
      title: t("Ask the AI Anything"),
      description: [
        t("Type any question about today's games"),
        t("Get instant analysis (uses StarCoins, your credits)")
      ],
      cta: t("START ASKING"),
      ctaLink: "/chat"
    },
    {
      step: "02",
      title: t("Quick Picks & xPicks"),
      description: [
        t("One-tap AI predictions for all games"),
        t("xPicks: Top 3-5 high probability bets per game (football/soccer)"),
        t("See confidence levels & reasoning")
      ],
      cta: t("TRY QUICKPICKS"),
      ctaLink: "/football"
    },
    {
      step: "03",
      title: t("Get StarCoins"),
      description: [
        t("StarCoins = credits for insights"),
        t("Free to start, buy more instantly anytime")
      ],
      cta: t("GET STARCOINS"),
      ctaLink: "/upgrade"
    },
    {
      step: "04",
      title: t("Explore Your Way"),
      description: [
        t("Quick checks or deep dives"),
        t("Props, matchups, trends — you decide")
      ],
      cta: t("EXPLORE"),
      ctaLink: "/"
    },
    {
      step: "05",
      title: t("Optional Pools (Beta)"),
      description: [
        t("Join free Pools to test your edge against others")
      ],
      cta: t("BETA"),
      ctaLink: "/market"
    }
  ];

  const quickStats = [
    { value: "60", unit: "SEC", label: t("TO FIRST INSIGHT") },
    { value: "0", unit: "", label: t("LEARNING CURVE") },
    { value: "100", unit: "%", label: t("AI-POWERED") },
    { value: "FREE", unit: "", label: t("TO START") }
  ];

  return (
    <div className="min-h-screen bg-black">
      {/* Terminal-style Header */}
      <header className="fixed top-0 z-50 w-full bg-black border-b border-white/5">
        <div className="max-w-screen-xl mx-auto px-6 py-3">
          <div className="flex items-center justify-between">
            <Link
              to="/"
              className="flex items-center text-[11px] text-white/50 hover:text-white/80 transition-all uppercase tracking-[0.15em]"
            >
              <ArrowLeft className="w-3 h-3 mr-2" />
              {t("BACK")}
            </Link>
            <div className="flex items-center gap-1.5">
              <span className="w-1 h-1 bg-emerald-500/70 animate-pulse" />
              <span className="text-[9px] text-emerald-500/50 uppercase tracking-[0.2em]">
                {t("QUICK START GUIDE")}
              </span>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section - Terminal Style */}
      <section className="min-h-[60vh] flex items-center justify-center px-8 pt-20 pb-12">
        <div className="text-center max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="mb-6"
          >
            <h1 className="text-5xl lg:text-7xl font-thin text-white/95 leading-none">
              <span className="block">{isBrazilian ? 'SEUS PRIMEIROS' : 'YOUR FIRST'}</span>
              <span className="block bg-gradient-to-r from-white to-emerald-400/70 bg-clip-text text-transparent">
                {isBrazilian ? '2 MINUTOS' : '2 MINUTES'}
              </span>
              <span className="block text-white/70">{isBrazilian ? 'NO PICKSTAR' : 'ON PICKSTAR'}</span>
            </h1>
          </motion.div>
          <p className="text-xs text-white/50 uppercase tracking-[0.2em] mb-8">
            {isBrazilian ? 'DO CADASTRO ATÉ SEU PRIMEIRO INSIGHT EM MENOS DE UM MINUTO' : 'FROM SIGNUP TO YOUR FIRST INSIGHT IN UNDER A MINUTE'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/chat"
              className="inline-flex items-center justify-center px-8 py-3 bg-gradient-to-r from-emerald-500/80 to-violet-500/80
                         text-[11px] font-medium text-white uppercase tracking-[0.15em]
                         hover:from-emerald-500 hover:to-violet-500 transition-all"
            >
              {isBrazilian ? 'COMEÇAR PESQUISA' : 'START RESEARCHING'}
              <ChevronRight className="ml-2 w-3 h-3" />
            </Link>
            <Link
              to="/upgrade"
              className="inline-flex items-center justify-center px-6 py-3 border border-violet-500/30
                         text-[11px] text-white/70 uppercase tracking-[0.15em]
                         hover:text-violet-300/80 hover:border-violet-400/50 transition-all"
            >
              <Coins className="mr-2 w-3 h-3" />
              {isBrazilian ? 'OBTER STARCOINS' : 'GET STARCOINS'}
            </Link>
          </div>
        </div>
      </section>

      {/* Quick Stats Bar - Terminal Style */}
      <section className="border-t border-white/10 py-8">
        <div className="max-w-screen-xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {quickStats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index }}
                className="border-l border-emerald-500/20 pl-6"
              >
                <div className="text-4xl font-thin text-white/95 mb-1">
                  {stat.value}<span className="text-lg text-emerald-400/50">{stat.unit}</span>
                </div>
                <div className="text-[9px] text-white/30 uppercase tracking-[0.2em]">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-screen-xl mx-auto px-6">
        {/* Introduction Section */}
        <section className="py-8 border-t border-white/5">
          <h2 className="text-2xl font-light text-white/95 mb-3">
            <span className="text-[11px] text-white/30 uppercase tracking-[0.3em] block mb-1">TIME TO VALUE</span>
            60 SECONDS
          </h2>
          <p className="text-xs text-white/50 uppercase tracking-[0.15em]">
            {t("NO SETUP • NO LEARNING CURVE • JUST AI-POWERED SPORTS INTELLIGENCE")}
          </p>
        </section>

        {/* How It Works Section - Data Grid Style */}
        <section className="py-8 border-t border-white/5">
          <h2 className="text-2xl font-light text-white/95 mb-4">
            <span className="text-[11px] text-white/30 uppercase tracking-[0.3em] block mb-1">PROCESS</span>
            5 STEPS TO YOUR FIRST INSIGHT
          </h2>
          <div className="border border-violet-500/10">
            {/* Header */}
            <div className="grid grid-cols-12 gap-4 px-4 py-3 bg-gradient-to-r from-emerald-500/[0.02] to-violet-500/[0.02] border-b border-violet-500/10">
              <span className="col-span-1 text-[9px] text-white/30 uppercase tracking-[0.2em]">STEP</span>
              <span className="col-span-3 text-[9px] text-white/30 uppercase tracking-[0.2em]">ACTION</span>
              <span className="col-span-6 text-[9px] text-white/30 uppercase tracking-[0.2em]">DETAILS</span>
              <span className="col-span-2 text-[9px] text-white/30 uppercase tracking-[0.2em]">START</span>
            </div>
            {/* Rows */}
            {steps.map((step, index) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: index * 0.1 }}
                className="grid grid-cols-12 gap-4 px-4 py-3 hover:bg-white/[0.02] group border-b border-white/[0.05] last:border-0"
              >
                <span className="col-span-1 text-emerald-400/80 font-mono text-xs">{step.step}</span>
                <span className="col-span-3 text-xs text-white/70">{step.title}</span>
                <div className="col-span-6">
                  {step.description.map((item, idx) => (
                    <div key={idx} className="text-[10px] text-white/50 mb-1">
                      <span className="text-emerald-500/30 mr-1">▪</span>
                      {item}
                    </div>
                  ))}
                </div>
                <div className="col-span-2">
                  <button
                    onClick={() => navigate(step.ctaLink)}
                    className="text-[11px] text-violet-400/70 hover:text-violet-400/90 transition-all uppercase tracking-wider"
                  >
                    {step.cta} →
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Example Walkthrough - Terminal Style */}
        <section className="py-8 border-t border-white/5">
          <h2 className="text-2xl font-light text-white/95 mb-4">
            <span className="text-[11px] text-white/30 uppercase tracking-[0.3em] block mb-1">{t("EXAMPLE")}</span>
            {t("LAKERS GAME TONIGHT")}
          </h2>
          <div className="space-y-3">
            {/* Example 1: Lakers Game */}
            <div className="border border-white/[0.05] hover:border-white/10 p-4 transition-all group">
              <div className="flex items-start gap-4">
                <span className="w-1 h-1 bg-emerald-500/70 rounded-full animate-pulse mt-2" />
                <div className="flex-1">
                  <p className="text-xs text-white/70 font-mono mb-2">
                    {t('USER: "What are the Lakers\' chances tonight?"')}
                  </p>
                  <p className="text-[11px] text-emerald-400/50 font-mono">
                    {t("AI: WIN PROBABILITY • KEY MATCHUPS • INJURIES • TOP PLAYS")}
                  </p>
                  <p className="text-[9px] text-white/30 mt-2 uppercase tracking-[0.15em]">
                    {t("RESPONSE TIME: <2 MINUTES")}
                  </p>
                </div>
              </div>
            </div>

            {/* Example 2: NFL Picks */}
            <div className="border border-white/[0.05] hover:border-white/10 p-4 transition-all group">
              <div className="flex items-start gap-4">
                <span className="w-1 h-1 bg-violet-500/70 rounded-full animate-pulse mt-2" />
                <div className="flex-1">
                  <p className="text-[9px] text-white/30 uppercase tracking-[0.15em] mb-1">
                    {t("NFL SUNDAY PICKS")}
                  </p>
                  <p className="text-xs text-white/70 font-mono mb-2">
                    {t('USER: "Give me your best NFL picks for Sunday"')}
                  </p>
                  <p className="text-[11px] text-violet-400/50 font-mono">
                    {t("AI: TOP 3 PICKS • CONFIDENCE SCORES • KEY FACTORS • WEATHER IMPACT")}
                  </p>
                  <p className="text-[9px] text-white/30 mt-2 uppercase tracking-[0.15em]">
                    {t("RESPONSE TIME: <2 MINUTES")}
                  </p>
                </div>
              </div>
            </div>

            {/* Example 3: Player Props */}
            <div className="border border-white/[0.05] hover:border-white/10 p-4 transition-all group">
              <div className="flex items-start gap-4">
                <span className="w-1 h-1 bg-emerald-500/70 rounded-full animate-pulse mt-2" />
                <div className="flex-1">
                  <p className="text-[9px] text-white/30 uppercase tracking-[0.15em] mb-1">
                    {t("PLAYER PROP ANALYSIS")}
                  </p>
                  <p className="text-xs text-white/70 font-mono mb-2">
                    {t('USER: "Will LeBron score over 25.5 points?"')}
                  </p>
                  <p className="text-[11px] text-emerald-400/50 font-mono">
                    {t("AI: RECENT AVERAGES • MATCHUP HISTORY • INJURY STATUS • RECOMMENDATION")}
                  </p>
                  <p className="text-[9px] text-white/30 mt-2 uppercase tracking-[0.15em]">
                    {t("RESPONSE TIME: <2 MINUTES")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Key Features Grid - Minimal Cards */}
        <section className="py-8 border-t border-white/5">
          <h2 className="text-2xl font-light text-white/95 mb-4">
            <span className="text-[11px] text-white/30 uppercase tracking-[0.3em] block mb-1">{t("FEATURES")}</span>
            {t("WHY PICKSTAR")}
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="border border-white/[0.05] hover:border-white/10 p-4 transition-all group"
            >
              <div className="flex items-center gap-3 mb-2">
                <MessageCircle className="w-3 h-3 text-emerald-500/50" />
                <h3 className="text-[11px] font-medium text-white/70 uppercase tracking-[0.15em]">
                  {t("NATURAL CONVERSATION")}
                </h3>
              </div>
              <p className="text-[10px] text-white/50 font-mono">
                {t("No slips, no jargon — just ask and get answers")}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.1 }}
              className="border border-white/[0.05] hover:border-white/10 p-4 transition-all group"
            >
              <div className="flex items-center gap-3 mb-2">
                <Target className="w-3 h-3 text-violet-500/50" />
                <h3 className="text-[11px] font-medium text-white/70 uppercase tracking-[0.15em]">
                  {t("SPORTS-FOCUSED AI")}
                </h3>
              </div>
              <p className="text-[10px] text-white/50 font-mono">
                {t("Real analysis, not generic chat")}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="border border-white/[0.05] hover:border-white/10 p-4 transition-all group"
            >
              <div className="flex items-center gap-3 mb-2">
                <Coins className="w-3 h-3 text-emerald-500/50" />
                <h3 className="text-[11px] font-medium text-white/70 uppercase tracking-[0.15em]">
                  {t("TRANSPARENT CREDITS")}
                </h3>
              </div>
              <p className="text-[10px] text-white/50 font-mono">
                {t("Simple, fair StarCoins")}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="border border-white/[0.05] hover:border-white/10 p-4 transition-all group"
            >
              <div className="flex items-center gap-3 mb-2">
                <Users className="w-3 h-3 text-violet-500/50" />
                <h3 className="text-[11px] font-medium text-white/70 uppercase tracking-[0.15em]">
                  {t("COMMUNITY INSIGHTS")}
                </h3>
              </div>
              <p className="text-[10px] text-white/50 font-mono">
                {t("Learn from what others are asking")}
              </p>
            </motion.div>
          </div>
        </section>

        {/* FAQ Section - Minimal Accordion */}
        <section className="py-8 border-t border-white/5">
          <h2 className="text-2xl font-light text-white/95 mb-4">
            <span className="text-[11px] text-white/30 uppercase tracking-[0.3em] block mb-1">{t("SUPPORT")}</span>
            {t("FAQ HIGHLIGHTS")}
          </h2>
          <div className="space-y-0 border border-white/[0.05]">
            <details className="group">
              <summary className="px-4 py-3 cursor-pointer flex justify-between items-center hover:bg-white/[0.02] transition-colors border-b border-white/[0.05]">
                <span className="text-xs text-white/70">{t("Do I need to know about betting to use PickStar?")}</span>
                <ChevronRight className="w-3 h-3 text-white/30 group-open:rotate-90 transition-transform" />
              </summary>
              <div className="px-4 py-3 text-[10px] text-white/50 font-mono border-b border-white/[0.05]">
                {t("No — it's research-first.")}
              </div>
            </details>

            <details className="group">
              <summary className="px-4 py-3 cursor-pointer flex justify-between items-center hover:bg-white/[0.02] transition-colors border-b border-white/[0.05]">
                <span className="text-xs text-white/70">{t("How do StarCoins work?")}</span>
                <ChevronRight className="w-3 h-3 text-white/30 group-open:rotate-90 transition-transform" />
              </summary>
              <div className="px-4 py-3 text-[10px] text-white/50 font-mono border-b border-white/[0.05]">
                {t("StarCoins are credits you use to ask questions and get Quick Picks. Think of them like tokens at an arcade — they power your experience.")}
              </div>
            </details>

            <details className="group">
              <summary className="px-4 py-3 cursor-pointer flex justify-between items-center hover:bg-white/[0.02] transition-colors border-b border-white/[0.05]">
                <span className="text-xs text-white/70">{t("What sports does PickStar cover?")}</span>
                <ChevronRight className="w-3 h-3 text-white/30 group-open:rotate-90 transition-transform" />
              </summary>
              <div className="px-4 py-3 text-[10px] text-white/50 font-mono border-b border-white/[0.05]">
                {t("NBA, NFL, MLB, NCAAF, Serie A, and more.")}
              </div>
            </details>

            <details className="group">
              <summary className="px-4 py-3 cursor-pointer flex justify-between items-center hover:bg-white/[0.02] transition-colors">
                <span className="text-xs text-white/70">{t("Can I try it for free?")}</span>
                <ChevronRight className="w-3 h-3 text-white/30 group-open:rotate-90 transition-transform" />
              </summary>
              <div className="px-4 py-3 text-[10px] text-white/50 font-mono">
                {t("Yes — you get StarCoins to start.")}
              </div>
            </details>
          </div>
        </section>

        {/* CTA Section - Terminal Style */}
        <section className="py-12 border-t border-white/5 text-center">
          <h2 className="text-2xl font-light text-white/95 mb-4">
            <span className="text-[11px] text-white/30 uppercase tracking-[0.3em] block mb-1">{t("ACTION")}</span>
            {t("READY? START NOW")}
          </h2>
          <Link
            to="/chat"
            className="inline-flex items-center justify-center px-8 py-3 bg-gradient-to-r from-emerald-500/80 to-violet-500/80
                       text-[11px] font-medium text-white uppercase tracking-[0.15em]
                       hover:from-emerald-500 hover:to-violet-500 transition-all"
          >
            {t("START RESEARCHING NOW")}
            <ChevronRight className="ml-2 w-3 h-3" />
          </Link>
        </section>
      </div>

      {/* SEO Footer */}
      <div className="border-t border-white/5">
        <SEOFooter language={isBrazilian ? 'pt-BR' : 'en'} />
      </div>
    </div>
  );
};

export default PickStarOnboarding;